import { CheckCircle2, Globe2, ShieldAlert } from "lucide-react";

export const tokenLabel = (value) => String(value || "unknown").replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

const statusCls = {
  registered: "bg-emerald-500/15 text-emerald-700",
  not_registered: "bg-red-500/10 text-red-700",
  pending: "bg-amber-500/15 text-amber-700",
  pending_verification: "bg-amber-500/15 text-amber-700",
  unknown: "bg-slate-500/10 text-slate-600",
};

export function StatusPill({ value, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${statusCls[value] || statusCls.unknown}`}>
      {tokenLabel(value)}
    </span>
  );
}

export function ModuleProfileCard({ profile }) {
  const india = profile?.india || {};
  const exporter = profile?.exporter || {};

  return (
    <section className="grid gap-4 lg:grid-cols-2" data-testid="module-profile-card">
      <div className="rounded-3xl border border-emerald-950/10 bg-white p-6 shadow-sm" data-testid="india-module-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-800/70">India obligation track</div>
            <h2 className="mt-2 font-display text-xl font-bold text-forest-deep" data-testid="india-module-title">
              {india.enabled ? `${tokenLabel(india.epr_role)} · ${tokenLabel(india.stream)}` : "India module not configured"}
            </h2>
          </div>
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-600/10 text-emerald-700">
            {india.enabled ? <CheckCircle2 className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
          </span>
        </div>
        {india.enabled ? (
          <div className="mt-5 space-y-3 text-sm text-forest-deep/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-forest-deep">CPCB registration</span>
              <StatusPill value={india.registration_status} testid="india-registration-status" />
            </div>
            <p data-testid="india-registration-number">
              Registration number: <span className="font-semibold text-forest-deep">{india.registration_number || "Not added"}</span>
            </p>
            <p className="rounded-2xl bg-parchment-base px-4 py-3 text-sm leading-relaxed">
              This track holds India-side duties, supplier checks and buyer checks for the selected stream.
            </p>
          </div>
        ) : (
          <p className="mt-5 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="india-module-empty">
            Clymaco staff will switch on the India module and set the registered entity details.
          </p>
        )}
      </div>

      <div className="rounded-3xl border border-emerald-950/10 bg-white p-6 shadow-sm" data-testid="exporter-module-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-800/70">Export obligation track</div>
            <h2 className="mt-2 font-display text-xl font-bold text-forest-deep" data-testid="exporter-module-title">
              {exporter.enabled ? "Exporter profile active" : "Exporter profile not active"}
            </h2>
          </div>
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-600/10 text-emerald-700">
            <Globe2 className="h-5 w-5" />
          </span>
        </div>
        {exporter.enabled ? (
          <div className="mt-5">
            <div className="flex flex-wrap gap-2" data-testid="export-market-list">
              {(exporter.markets || []).length ? (
                exporter.markets.map((market) => (
                  <span key={market} className="rounded-full bg-emerald-900/5 px-3 py-1.5 text-xs font-semibold text-forest-deep" data-testid={`export-market-${market.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {market}
                  </span>
                ))
              ) : (
                <span className="rounded-full bg-amber-500/15 px-3 py-1.5 text-xs font-semibold text-amber-800">Export markets not added yet</span>
              )}
            </div>
            {exporter.notes && <p className="mt-3 text-sm text-forest-deep/65" data-testid="export-notes">{exporter.notes}</p>}
            <p className="mt-4 rounded-2xl bg-parchment-base px-4 py-3 text-sm leading-relaxed text-forest-deep/70">
              Country-specific duties stay separate from India obligations and can be attached market by market next.
            </p>
          </div>
        ) : (
          <p className="mt-5 rounded-2xl bg-parchment-base px-4 py-3 text-sm text-forest-deep/60" data-testid="exporter-module-empty">
            If this organisation exports, Clymaco staff can activate the export track and list its markets.
          </p>
        )}
      </div>
    </section>
  );
}
